import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { doc, getDoc } from '../firebase.config';
import {
  ArrowLeft,
  ArrowRight,
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  Calendar,
  Database,
  Download,
  Filter,
  HardDrive,
  Info,
  Layers,
  LineChart,
  Minus,
  RefreshCw,
  Sparkles,
  Target,
  TrendingUp
} from 'lucide-react';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { db } from '../firebase.config';
import {
  aggregateUsageByYear,
  aggregateDailyToWeekly,
  filterDailyRowsByRange,
  getCompanyUsageHistoryDetailed,
  getCurrentMonth
} from '../utils/usageTracker';
import { computePlanSuggestion } from '../utils/planSuggestion';

const nf = (n) => (typeof n === 'number' && !Number.isNaN(n) ? n.toLocaleString('en-IN') : '0');

function buildUsageCsv(rows) {
  const header = ['Month', 'Month ID', 'Operations', 'Reads', 'Writes', 'Deletes', 'Storage (MB)'];
  const lines = [header.join(',')];
  for (const r of rows) {
    const cells = [
      `"${(r.monthLabel || '').replace(/"/g, '""')}"`,
      r.monthId,
      r.operations,
      r.reads,
      r.writes,
      r.deletes,
      typeof r.storageMb === 'number' ? r.storageMb.toFixed(2) : '0'
    ];
    lines.push(cells.join(','));
  }
  return lines.join('\n');
}

function buildPeriodCsv(rows, granularity) {
  const header =
    granularity === 'weekly'
      ? ['Week start', 'Week label', 'Operations', 'Storage (MB)', 'Days in range']
      : ['Date', 'Operations', 'Storage (MB)'];
  const lines = [header.join(',')];
  for (const r of rows) {
    if (granularity === 'weekly') {
      lines.push(
        [r.weekStart, `"${(r.weekLabel || '').replace(/"/g, '""')}"`, r.operations, r.storageMb.toFixed(2), r.dayCount].join(',')
      );
    } else {
      lines.push([r.date, r.operations, r.storageMb.toFixed(2)].join(','));
    }
  }
  return lines.join('\n');
}

function monthDateBounds(monthId) {
  const [y, m] = monthId.split('-').map((x) => parseInt(x, 10));
  const start = `${monthId}-01`;
  const lastDay = new Date(y, m, 0).getDate();
  const end = `${monthId}-${String(lastDay).padStart(2, '0')}`;
  return { start, end };
}

function metricValue(row, metric) {
  if (metric === 'reads') return row.reads;
  if (metric === 'writes') return row.writes;
  if (metric === 'deletes') return row.deletes;
  return row.operations;
}

function downloadTextFile(filename, text) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Last N months operations as SVG sparkline */
function OperationsSparkline({ series, metric = 'total' }) {
  const w = 360;
  const h = 96;
  const padX = 12;
  const padY = 10;
  const n = series.length;
  if (n === 0) return null;
  const max = Math.max(1, ...series.map((s) => metricValue(s, metric)));
  const innerW = w - 2 * padX;
  const innerH = h - 2 * padY;
  const xAt = (i) => (n === 1 ? padX + innerW / 2 : padX + (i / (n - 1)) * innerW);
  const yAt = (ops) => padY + innerH * (1 - ops / max);
  const points = series.map((s, i) => `${xAt(i)},${yAt(metricValue(s, metric))}`).join(' ');
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="w-full max-w-lg h-24 text-indigo-500"
      role="img"
      aria-label="Operations trend for recent months"
    >
      <defs>
        <linearGradient id="usageSparkFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgb(99, 102, 241)" stopOpacity="0.25" />
          <stop offset="100%" stopColor="rgb(99, 102, 241)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        fill="url(#usageSparkFill)"
        points={`${xAt(0)},${padY + innerH} ${points} ${xAt(n - 1)},${padY + innerH}`}
      />
      <polyline fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" points={points} />
      {series.map((s, i) => (
        <circle
          key={s.monthId}
          cx={xAt(i)}
          cy={yAt(metricValue(s, metric))}
          r="3.5"
          fill="white"
          stroke="currentColor"
          strokeWidth="2"
        />
      ))}
    </svg>
  );
}

/** Stacked proportion bar for reads / writes / deletes */
function OperationMixBar({ reads, writes, deletes, label }) {
  const t = reads + writes + deletes;
  if (t <= 0) {
    return <p className="text-sm text-slate-500">No read/write/delete breakdown recorded yet.</p>;
  }
  const pr = Math.round((reads / t) * 1000) / 10;
  const pw = Math.round((writes / t) * 1000) / 10;
  const pd = Math.round((deletes / t) * 1000) / 10;
  return (
    <div>
      {label && <p className="text-xs font-medium text-slate-600 mb-2">{label}</p>}
      <div className="flex h-4 rounded-full overflow-hidden border border-slate-200">
        <div className="bg-sky-500 transition-all" style={{ width: `${pr}%` }} title={`Reads ${pr}%`} />
        <div className="bg-amber-400 transition-all" style={{ width: `${pw}%` }} title={`Writes ${pw}%`} />
        <div className="bg-rose-400 transition-all" style={{ width: `${pd}%` }} title={`Deletes ${pd}%`} />
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-600">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-sky-500" /> Reads {pr}%
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-amber-400" /> Writes {pw}%
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-rose-400" /> Deletes {pd}%
        </span>
      </div>
    </div>
  );
}

function loadSubscriptionSnapshot(companyId) {
  const ref = doc(db, 'companies', companyId, 'subscription_details', 'subscription');
  return getDoc(ref);
}

export default function CompanyUsageStatisticsPage() {
  const navigate = useNavigate();
  const { currentCompany, state, companyProfile } = useEnhancedCompany();
  // Match SubscriptionStatusModern / rest of app: context does not expose top-level `companyInfo`
  const companyId =
    currentCompany?.companyId ||
    currentCompany?.id ||
    state?.companyInfo?.companyId ||
    state?.companyInfo?.id ||
    null;
  const companyName =
    companyProfile?.firmName || companyProfile?.name || currentCompany?.name || companyId || 'Company';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [period, setPeriod] = useState(null);
  const [yearFilter, setYearFilter] = useState('all');
  const [monthsWindow, setMonthsWindow] = useState('12');
  const [metricView, setMetricView] = useState('total');
  const [selectedMonthId, setSelectedMonthId] = useState('');
  const [periodGranularity, setPeriodGranularity] = useState('daily');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');

  const load = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [rows, subSnap] = await Promise.all([
        getCompanyUsageHistoryDetailed(db, companyId),
        loadSubscriptionSnapshot(companyId)
      ]);

      setMonthly(rows);

      const currentMonthKey = getCurrentMonth();
      const currentMonthRow = rows.find((r) => r.monthId === currentMonthKey) || null;
      const latestMonthId = rows.length > 0 ? rows[rows.length - 1].monthId : '';
      setSelectedMonthId((prev) => prev || currentMonthKey || latestMonthId);

      if (subSnap.exists()) {
        const s = subSnap.data();
        const ent = s.totalEntitlements || s.basePlan?.limits || {};
        const use = s.currentMonthUsage || {};
        const topups = s.topups || [];
        const billingOps = use.firebase_operations ?? 0;
        const trackedOps = currentMonthRow?.operations ?? 0;
        setPeriod({
          planName: s.basePlan?.planName || s.planName || '—',
          planId: s.basePlan?.planId || s.planId || null,
          baseLimits: s.basePlan?.limits || {},
          usedOps: billingOps,
          trackedOps,
          limitOps: ent.firebase_operations ?? 0,
          usedStorageMb: use.storage_mb ?? 0,
          limitStorageMb: ent.storage_mb ?? 0,
          hasTopups: topups.some((t) => t.status === 'active')
        });
      } else {
        setPeriod(null);
      }
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to load usage statistics');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const availableYears = useMemo(() => {
    const years = new Set(monthly.map((r) => r.monthId.slice(0, 4)));
    return Array.from(years).sort();
  }, [monthly]);

  const filteredMonthly = useMemo(() => {
    let rows = [...monthly];
    if (yearFilter !== 'all') {
      rows = rows.filter((r) => r.monthId.startsWith(`${yearFilter}-`));
    }
    if (monthsWindow !== 'all') {
      const n = parseInt(monthsWindow, 10);
      rows = rows.slice(-n);
    }
    return rows;
  }, [monthly, yearFilter, monthsWindow]);

  const selectedMonth = useMemo(
    () => monthly.find((r) => r.monthId === selectedMonthId) || null,
    [monthly, selectedMonthId]
  );

  const yearly = useMemo(() => aggregateUsageByYear(filteredMonthly), [filteredMonthly]);

  const totals = useMemo(() => {
    const totalOps = filteredMonthly.reduce((a, r) => a + r.operations, 0);
    const months = filteredMonthly.length;
    return {
      totalOps,
      months,
      avgOps: months > 0 ? Math.round(totalOps / months) : 0
    };
  }, [filteredMonthly]);

  const chartSeries = useMemo(() => filteredMonthly, [filteredMonthly]);
  const maxMonthOps = useMemo(() => {
    const m = Math.max(0, ...chartSeries.map((r) => metricValue(r, metricView)));
    return m > 0 ? m : 1;
  }, [chartSeries, metricView]);

  useEffect(() => {
    if (!selectedMonthId) return;
    const { start, end } = monthDateBounds(selectedMonthId);
    setCustomDateFrom(start);
    setCustomDateTo(end);
  }, [selectedMonthId]);

  const filteredDailyRows = useMemo(() => {
    if (!selectedMonth?.dailyRows) return [];
    return filterDailyRowsByRange(selectedMonth.dailyRows, customDateFrom, customDateTo);
  }, [selectedMonth, customDateFrom, customDateTo]);

  const filteredWeeklyRows = useMemo(
    () => aggregateDailyToWeekly(filteredDailyRows),
    [filteredDailyRows]
  );

  const periodRows = periodGranularity === 'weekly' ? filteredWeeklyRows : filteredDailyRows;
  const periodTotalOps = useMemo(
    () => periodRows.reduce((s, r) => s + r.operations, 0),
    [periodRows]
  );

  const handleExportPeriodCsv = () => {
    const csv = buildPeriodCsv(
      periodGranularity === 'weekly' ? filteredWeeklyRows : [...filteredDailyRows].reverse(),
      periodGranularity
    );
    const safe = companyId.replace(/[^a-zA-Z0-9-_]/g, '_');
    downloadTextFile(`acctoo-usage-${periodGranularity}-${selectedMonthId}-${safe}.csv`, csv);
  };

  const analytics = useMemo(() => {
    const totalR = filteredMonthly.reduce((a, r) => a + r.reads, 0);
    const totalW = filteredMonthly.reduce((a, r) => a + r.writes, 0);
    const totalD = filteredMonthly.reduce((a, r) => a + r.deletes, 0);
    const rwd = totalR + totalW + totalD;

    let peak = null;
    for (const r of filteredMonthly) {
      if (!peak || r.operations > peak.operations) {
        peak = { monthLabel: r.monthLabel, monthId: r.monthId, operations: r.operations };
      }
    }

    const sorted = [...filteredMonthly].sort((a, b) => a.monthId.localeCompare(b.monthId));
    const last = sorted[sorted.length - 1];
    const prev = sorted.length >= 2 ? sorted[sorted.length - 2] : null;

    let momPct = null;
    if (last && prev) {
      if (prev.operations > 0) {
        momPct = ((last.operations - prev.operations) / prev.operations) * 100;
      } else if (last.operations > 0) {
        momPct = null;
      }
    }

    const last3 = sorted.slice(-3);
    const prev3 = sorted.length >= 6 ? sorted.slice(-6, -3) : [];
    const avgLast3 =
      last3.length > 0 ? last3.reduce((a, r) => a + r.operations, 0) / last3.length : 0;
    const avgPrev3 =
      prev3.length > 0 ? prev3.reduce((a, r) => a + r.operations, 0) / prev3.length : null;
    let vsPrev3Pct = null;
    if (avgPrev3 && avgPrev3 > 0) {
      vsPrev3Pct = ((avgLast3 - avgPrev3) / avgPrev3) * 100;
    }

    const opsList = sorted.map((r) => r.operations);
    const medianOps = (() => {
      if (opsList.length === 0) return 0;
      const a = [...opsList].sort((x, y) => x - y);
      const mid = Math.floor(a.length / 2);
      return a.length % 2 ? a[mid] : Math.round((a[mid - 1] + a[mid]) / 2);
    })();

    return {
      totalR,
      totalW,
      totalD,
      rwd,
      peak,
      momPct,
      lastMonth: last,
      prevMonth: prev,
      avgLast3: Math.round(avgLast3),
      avgPrev3: avgPrev3 != null ? Math.round(avgPrev3) : null,
      vsPrev3Pct,
      medianOps
    };
  }, [filteredMonthly]);

  const planSuggestion = useMemo(() => {
    if (loading) return null;
    return computePlanSuggestion({
      monthlyRows: filteredMonthly,
      analytics,
      totals,
      period
    });
  }, [loading, filteredMonthly, analytics, totals, period]);

  const handleExportCsv = () => {
    const csv = buildUsageCsv(filteredMonthly);
    const safe = companyId.replace(/[^a-zA-Z0-9-_]/g, '_');
    downloadTextFile(`acctoo-usage-${safe}.csv`, csv);
  };

  if (!companyId) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
            <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900">
              Select a company to view usage statistics for that business.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
                aria-label="Back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                  Usage statistics
                </h1>
                <p className="text-sm text-slate-600 mt-1">
                  {companyName}
                  <span className="text-slate-400"> · </span>
                  <span className="font-mono text-xs">{companyId}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleExportCsv}
                disabled={loading || monthly.length === 0}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                title="Download monthly usage as CSV"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
              <button
                type="button"
                onClick={() => load()}
                disabled={loading}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-6 space-y-6">
        <div className="rounded-xl border border-blue-100 bg-blue-50/80 p-4 flex gap-3">
          <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-950 space-y-1">
            <p className="font-medium">How usage is counted</p>
            <p className="text-blue-900/90">
              Operations = <strong>reads + writes + deletes</strong> from your team&apos;s app activity.
              View by month, custom date range, or week. Your plan usage is updated as you work.
            </p>
          </div>
        </div>

        {!loading && (
          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-semibold text-slate-800">Filters &amp; view</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <label className="text-xs text-slate-600 space-y-1">
                Year
                <select
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                >
                  <option value="all">All years</option>
                  {availableYears.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs text-slate-600 space-y-1">
                Months shown
                <select
                  value={monthsWindow}
                  onChange={(e) => setMonthsWindow(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                >
                  <option value="3">Last 3 months</option>
                  <option value="6">Last 6 months</option>
                  <option value="12">Last 12 months</option>
                  <option value="all">All months on file</option>
                </select>
              </label>
              <label className="text-xs text-slate-600 space-y-1">
                Chart metric
                <select
                  value={metricView}
                  onChange={(e) => setMetricView(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                >
                  <option value="total">Total operations</option>
                  <option value="reads">Reads only</option>
                  <option value="writes">Writes only</option>
                  <option value="deletes">Deletes only</option>
                </select>
              </label>
              <label className="text-xs text-slate-600 space-y-1">
                Drill-down month
                <select
                  value={selectedMonthId}
                  onChange={(e) => setSelectedMonthId(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                >
                  {[...monthly].reverse().map((r) => (
                    <option key={r.monthId} value={r.monthId}>
                      {r.monthLabel}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </section>
        )}

        {!loading && period && (
          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <Layers className="w-5 h-5 text-emerald-600" />
              <h2 className="text-lg font-semibold text-slate-900">Current plan period</h2>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase text-slate-500">Plan usage</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {nf(period.usedOps)}
                  <span className="text-slate-400 font-normal text-lg"> / {nf(period.limitOps)}</span>
                </p>
                <p className="text-xs text-slate-500 mt-1">Operations counted toward your subscription</p>
              </div>
              <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase text-slate-500">Activity this month</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{nf(period.trackedOps ?? 0)}</p>
                <p className="text-xs text-slate-500 mt-1">Reads + writes + deletes recorded in the app</p>
              </div>
            </div>
          </section>
        )}

        {!loading && selectedMonth && (
          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-semibold text-slate-900">
                  Custom usage — {selectedMonth.monthLabel}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleExportPeriodCsv}
                  disabled={periodRows.length === 0}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export {periodGranularity}
                </button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <label className="text-xs text-slate-600 space-y-1">
                  View
                  <select
                    value={periodGranularity}
                    onChange={(e) => setPeriodGranularity(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </label>
                <label className="text-xs text-slate-600 space-y-1">
                  From
                  <input
                    type="date"
                    value={customDateFrom}
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  />
                </label>
                <label className="text-xs text-slate-600 space-y-1">
                  To
                  <input
                    type="date"
                    value={customDateTo}
                    onChange={(e) => setCustomDateTo(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  />
                </label>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 flex flex-col justify-center">
                  <p className="text-xs text-slate-500">Total in range</p>
                  <p className="text-lg font-bold text-slate-900 tabular-nums">{nf(periodTotalOps)} ops</p>
                </div>
              </div>

              {periodRows.length === 0 ? (
                <p className="text-sm text-slate-500">No usage in the selected date range.</p>
              ) : periodGranularity === 'weekly' ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 text-left text-slate-600">
                        <th className="px-3 py-2 font-semibold">Week</th>
                        <th className="px-3 py-2 font-semibold text-right">Operations</th>
                        <th className="px-3 py-2 font-semibold text-right">Storage (MB)</th>
                        <th className="px-3 py-2 font-semibold text-right">Days</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...filteredWeeklyRows].reverse().map((w) => (
                        <tr key={w.weekStart} className="border-t border-slate-100">
                          <td className="px-3 py-2 text-slate-800">{w.weekLabel}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{nf(w.operations)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{w.storageMb.toFixed(1)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-500">{w.dayCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 text-left text-slate-600">
                        <th className="px-3 py-2 font-semibold">Date</th>
                        <th className="px-3 py-2 font-semibold text-right">Operations</th>
                        <th className="px-3 py-2 font-semibold text-right">Storage (MB)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...filteredDailyRows].reverse().map((d) => (
                        <tr key={d.date} className="border-t border-slate-100">
                          <td className="px-3 py-2 font-mono text-xs">{d.date}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{nf(d.operations)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{d.storageMb.toFixed(1)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        )}

        {!loading && planSuggestion && (
          <section className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50/90 via-white to-indigo-50/50 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-violet-100 flex flex-wrap items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" />
              <h2 className="text-lg font-semibold text-slate-900">Plan suggestion</h2>
              <span className="text-xs font-medium text-violet-700 bg-violet-100 px-2 py-0.5 rounded-full">
                Based on your usage
              </span>
            </div>
            <div className="p-5 space-y-4">
              {planSuggestion.status === 'no_subscription' && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-700">{planSuggestion.summary}</p>
                  <Link
                    to="/subscription"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-violet-700 hover:text-violet-900"
                  >
                    Open subscription
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              )}

              {planSuggestion.status === 'insufficient_data' && (
                <>
                  <p className="text-sm text-slate-700">{planSuggestion.summary}</p>
                  <p className="text-xs text-slate-500">
                    Estimates use ~{planSuggestion.headroomOpsPercent}% buffer on operations and ~
                    {planSuggestion.headroomStoragePercent}% on storage once data is available.
                  </p>
                </>
              )}

              {(planSuggestion.status === 'already_capable' ||
                planSuggestion.status === 'recommend_upgrade') && (
                <>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Current plan (detected)
                      </p>
                      <p className="font-semibold text-slate-900 mt-0.5">
                        {planSuggestion.currentTierName || '—'}
                      </p>
                    </div>
                    {planSuggestion.status === 'recommend_upgrade' && planSuggestion.suggestedTierName && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-violet-700">
                          Suggested upgrade
                        </p>
                        <p className="font-semibold text-violet-900 mt-0.5">
                          {planSuggestion.suggestedTierName}
                          <span className="text-slate-500 font-normal">
                            {' '}
                            — from ₹{planSuggestion.suggestedPrice}/mo (indicative) ·{' '}
                            {nf(planSuggestion.suggestedOpsLimit)} ops ·{' '}
                            {planSuggestion.suggestedStorageMb >= 1024
                              ? `${(planSuggestion.suggestedStorageMb / 1024).toFixed(1)} GB`
                              : `${planSuggestion.suggestedStorageMb} MB`}{' '}
                            storage
                          </span>
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          We pick one tier above the smallest plan that fits your projected usage (with buffer).
                        </p>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">{planSuggestion.summary}</p>
                  {planSuggestion.reasons?.length > 0 && (
                    <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside">
                      {planSuggestion.reasons.map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  )}
                  {planSuggestion.status === 'recommend_upgrade' && (
                    <div className="flex flex-wrap gap-3 pt-1">
                      <Link
                        to="/upgrade-plan"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 shadow-sm"
                      >
                        View upgrade options
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                      <Link
                        to="/subscription"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50"
                      >
                        Subscription
                      </Link>
                    </div>
                  )}
                  {planSuggestion.status === 'already_capable' && (
                    <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
                      No upgrade needed for typical usage right now. Recheck after busy months or if limits feel tight.
                    </p>
                  )}
                </>
              )}
            </div>
          </section>
        )}

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-500">
            <RefreshCw className="w-10 h-10 animate-spin mb-3" />
            Loading statistics…
          </div>
        ) : (
          <>
            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wide">
                  <Database className="w-4 h-4" />
                  All-time (tracked)
                </div>
                <p className="text-2xl font-bold text-slate-900 mt-2">{nf(totals.totalOps)}</p>
                <p className="text-xs text-slate-500 mt-1">Sum of every month stored under Usage</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wide">
                  <Calendar className="w-4 h-4" />
                  Months on file
                </div>
                <p className="text-2xl font-bold text-slate-900 mt-2">{nf(totals.months)}</p>
                <p className="text-xs text-slate-500 mt-1">Calendar months with a usage record</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wide">
                  <TrendingUp className="w-4 h-4" />
                  Avg / month
                </div>
                <p className="text-2xl font-bold text-slate-900 mt-2">{nf(totals.avgOps)}</p>
                <p className="text-xs text-slate-500 mt-1">Average across months with data</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wide">
                  <Layers className="w-4 h-4" />
                  Current billing period
                </div>
                {period ? (
                  <>
                    <p className="text-lg font-bold text-slate-900 mt-2">
                      {nf(period.usedOps)}
                      <span className="text-slate-400 font-normal"> / </span>
                      {nf(period.limitOps)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Plan: {period.planName}</p>
                    <p className="text-xs text-slate-600 mt-2 flex items-center gap-1">
                      <HardDrive className="w-3.5 h-3.5" />
                      Storage {period.usedStorageMb.toFixed(1)} / {period.limitStorageMb} MB
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-slate-500 mt-2">No subscription document found.</p>
                )}
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <LineChart className="w-5 h-5 text-violet-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Analytics &amp; insights</h2>
                </div>
                <p className="text-xs text-slate-500">Derived from your stored monthly usage</p>
              </div>
              <div className="p-5 space-y-6">
                {period && period.limitOps > 0 && (
                  <div>
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-2">
                      <Target className="w-4 h-4 text-violet-600" />
                      Plan quota (this billing period)
                    </div>
                    <div className="flex justify-between text-xs text-slate-600 mb-1">
                      <span>
                        {nf(period.usedOps)} of {nf(period.limitOps)} operations
                      </span>
                      <span>
                        {Math.min(100, Math.round((period.usedOps / period.limitOps) * 100))}%
                      </span>
                    </div>
                    <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          period.usedOps / period.limitOps >= 0.9
                            ? 'bg-rose-500'
                            : period.usedOps / period.limitOps >= 0.75
                              ? 'bg-amber-400'
                              : 'bg-violet-500'
                        }`}
                        style={{
                          width: `${Math.min(100, (period.usedOps / period.limitOps) * 100)}%`
                        }}
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="rounded-xl border border-slate-100 bg-slate-50/90 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      vs prior month
                    </p>
                    <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                      {analytics.momPct != null && !Number.isNaN(analytics.momPct) ? (
                        <>
                          {analytics.momPct > 0 ? (
                            <ArrowUpRight className="w-5 h-5 text-indigo-600" />
                          ) : analytics.momPct < 0 ? (
                            <ArrowDownRight className="w-5 h-5 text-slate-600" />
                          ) : (
                            <Minus className="w-5 h-5 text-slate-400" />
                          )}
                          <span className="text-2xl font-bold text-slate-900">
                            {analytics.momPct > 0 ? '+' : ''}
                            {analytics.momPct.toFixed(1)}%
                          </span>
                        </>
                      ) : (
                        <span className="text-sm text-slate-500">Not enough history</span>
                      )}
                    </div>
                    {analytics.prevMonth && (
                      <p className="text-xs text-slate-500 mt-2">
                        vs {analytics.prevMonth.monthLabel}
                      </p>
                    )}
                  </div>

                  <div className="rounded-xl border border-slate-100 bg-slate-50/90 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Last 3 mo vs prior 3 mo
                    </p>
                    <div className="mt-2">
                      {analytics.avgPrev3 != null && monthly.length >= 6 ? (
                        <>
                          <p className="text-2xl font-bold text-slate-900">
                            {analytics.vsPrev3Pct != null && !Number.isNaN(analytics.vsPrev3Pct) ? (
                              <>
                                {analytics.vsPrev3Pct > 0 ? '+' : ''}
                                {analytics.vsPrev3Pct.toFixed(1)}%
                              </>
                            ) : (
                              '—'
                            )}
                          </p>
                          <p className="text-xs text-slate-600 mt-1">
                            Avg {nf(analytics.avgLast3)} vs {nf(analytics.avgPrev3)} ops / mo
                          </p>
                        </>
                      ) : (
                        <p className="text-sm text-slate-500">Needs at least 6 months of data</p>
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-100 bg-slate-50/90 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Typical month (median)
                    </p>
                    <p className="text-2xl font-bold text-slate-900 mt-2">{nf(analytics.medianOps)}</p>
                    <p className="text-xs text-slate-500 mt-1">Operations per month</p>
                  </div>

                  <div className="rounded-xl border border-slate-100 bg-slate-50/90 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Busiest month
                    </p>
                    {analytics.peak ? (
                      <>
                        <p className="text-lg font-bold text-slate-900 mt-2 leading-tight">
                          {nf(analytics.peak.operations)}
                        </p>
                        <p className="text-xs text-slate-600 mt-1">{analytics.peak.monthLabel}</p>
                      </>
                    ) : (
                      <p className="text-sm text-slate-500 mt-2">—</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-2 border-t border-slate-100">
                  <div>
                    <p className="text-sm font-medium text-slate-800 mb-3">Operations trend</p>
                    {chartSeries.length === 0 ? (
                      <p className="text-sm text-slate-500">No monthly data to chart yet.</p>
                    ) : (
                      <>
                        <OperationsSparkline series={chartSeries} metric={metricView} />
                        <p className="text-xs text-slate-500 mt-2">
                          {chartSeries.length} month{chartSeries.length !== 1 ? 's' : ''} in view · peak:{' '}
                          {nf(maxMonthOps)}
                        </p>
                      </>
                    )}
                  </div>
                  <div>
                    <OperationMixBar
                      reads={analytics.totalR}
                      writes={analytics.totalW}
                      deletes={analytics.totalD}
                      label="Operation mix (all months with breakdown)"
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-semibold text-slate-900">
                  Monthly trend ({metricView === 'total' ? 'operations' : metricView})
                </h2>
              </div>
              <div className="p-5">
                {chartSeries.length === 0 ? (
                  <p className="text-sm text-slate-500">
                    No monthly usage history yet. Activity will appear here as your team uses the app.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {chartSeries.map((row) => {
                      const val = metricValue(row, metricView);
                      const pct = Math.round((val / maxMonthOps) * 100);
                      return (
                        <div key={row.monthId}>
                          <div className="flex justify-between text-sm mb-1 gap-2">
                            <span className="font-medium text-slate-700">{row.monthLabel}</span>
                            <span className="text-slate-600 tabular-nums">{nf(val)}</span>
                          </div>
                          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-600" />
                <h2 className="text-lg font-semibold text-slate-900">Yearly totals</h2>
              </div>
              <div className="p-5">
                {yearly.length === 0 ? (
                  <p className="text-sm text-slate-500">No yearly totals yet.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {yearly.map((y) => (
                      <div
                        key={y.year}
                        className="rounded-xl border border-slate-100 bg-slate-50/80 p-4"
                      >
                        <div className="flex justify-between items-baseline">
                          <span className="text-xl font-bold text-slate-900">{y.year}</span>
                          <span className="text-sm text-slate-500">{y.monthsCount} month(s)</span>
                        </div>
                        <p className="text-2xl font-semibold text-slate-800 mt-2">
                          {nf(y.operations)} <span className="text-sm font-normal text-slate-500">operations</span>
                        </p>
                        <p className="text-xs text-slate-600 mt-2">
                          Busiest month:{' '}
                          {y.peakMonth.monthLabel ? (
                            <>
                              {y.peakMonth.monthLabel} ({nf(y.peakMonth.operations)})
                            </>
                          ) : (
                            '—'
                          )}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
                          <span className="px-2 py-0.5 rounded bg-white border border-slate-200">
                            R {nf(y.reads)}
                          </span>
                          <span className="px-2 py-0.5 rounded bg-white border border-slate-200">
                            W {nf(y.writes)}
                          </span>
                          <span className="px-2 py-0.5 rounded bg-white border border-slate-200">
                            D {nf(y.deletes)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
                <Database className="w-5 h-5 text-slate-700" />
                <h2 className="text-lg font-semibold text-slate-900">Monthly detail</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-left text-slate-600">
                      <th className="px-4 py-3 font-semibold">Month</th>
                      <th className="px-4 py-3 font-semibold text-right">Operations</th>
                      <th className="px-4 py-3 font-semibold text-right">Reads</th>
                      <th className="px-4 py-3 font-semibold text-right">Writes</th>
                      <th className="px-4 py-3 font-semibold text-right">Deletes</th>
                      <th className="px-4 py-3 font-semibold text-right">Storage (MB)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMonthly.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                          No rows yet.
                        </td>
                      </tr>
                    ) : (
                      [...filteredMonthly].reverse().map((row) => (
                        <tr key={row.monthId} className="border-t border-slate-100 hover:bg-slate-50/80">
                          <td className="px-4 py-3 font-medium text-slate-800">{row.monthLabel}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{nf(row.operations)}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600">{nf(row.reads)}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600">{nf(row.writes)}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600">{nf(row.deletes)}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                            {row.storageMb.toFixed(1)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
